import type { DateTime } from '@cheqroom/date-time';

export type BookingDates = {
	fromDate?: DateTime;
	toDate?: DateTime;
	due?: DateTime;
};

type DateSnapper = (date: DateTime) => DateTime;

export type BusinessHoursSnappers = {
	startOfDay: DateSnapper;
	endOfDay: DateSnapper;
};

export function snapBookingDates(dates: BookingDates, businessSnappers: BusinessHoursSnappers): BookingDates {
	const { startOfDay, endOfDay } = businessSnappers;
	return {
		fromDate: dates.fromDate && startOfDay(dates.fromDate),
		toDate: dates.toDate && endOfDay(dates.toDate),
		due: dates.due && endOfDay(dates.due),
	};
}

/**
 * Hour-precision workspaces preserve exact times unless `force` is set (e.g. month-view
 * selections where FullCalendar only provides midnight timestamps).
 */
export function resolveSnapBookingDates(
	dates: BookingDates,
	useHours: boolean,
	businessSnappers: BusinessHoursSnappers,
	{ force = false }: { force?: boolean } = {}
): BookingDates {
	if (useHours && !force) {
		return dates;
	}
	return snapBookingDates(dates, businessSnappers);
}

/**
 * Returns the date to compare against when deciding overdue status.
 * Day-precision bookings compare against business close; hour-precision uses the stored time.
 */
export function resolveDueComparisonDate(
	date: DateTime | null | undefined,
	useHours: boolean,
	toBusinessEnd: DateSnapper
): DateTime | undefined {
	if (!date) return undefined;
	if (useHours) return date;
	return toBusinessEnd(date);
}
